import { useEffect, useMemo, useState } from "react";
import type { RoundSnapshot } from "../../domain/round/round-reducer.js";
import { roundJournal } from "../data/local-database.js";
import {
  activateWaitingUpdate,
  requestPersistentStorage,
} from "./pwa-client.js";
import { canActivateUpdate, updateMessage } from "./pwa-update-policy.js";

export interface PwaStatus {
  updateReady: boolean;
  updateSafe: boolean;
  updateMessage: string;
  activateUpdate(): Promise<void>;
}

export function usePwaStatus(
  accountId: string,
  snapshot: RoundSnapshot | null,
  queued: boolean,
): PwaStatus {
  const [updateReady, setUpdateReady] = useState(false);
  useEffect(() => {
    const ready = () => setUpdateReady(true);
    window.addEventListener("fairway:update-ready", ready);
    void requestPersistentStorage();
    return () => window.removeEventListener("fairway:update-ready", ready);
  }, []);
  const safety = useMemo(
    () => ({
      hasActiveRound:
        snapshot?.state === "draft" || snapshot?.state === "active",
      hasUnsyncedCommands: queued,
    }),
    [snapshot?.state, queued],
  );
  return {
    updateReady,
    updateSafe: canActivateUpdate(safety),
    updateMessage: updateMessage(safety),
    activateUpdate: async () => {
      const hasUnsyncedCommands = await roundJournal.hasUnsynced(accountId);
      if (!canActivateUpdate({ ...safety, hasUnsyncedCommands })) return;
      await activateWaitingUpdate();
    },
  };
}
